import React, { PureComponent } from "react";
import PropTypes from "prop-types";

import {
  StyledTerminalWrapper,
  StyledTerminal,
  StyledTerminalInner,
  StyledInputWrapper,
  StyledInput,
  StyledPrompt,
  StyledLoadingCursor,
  StyledBlinkCursor,
  StyledCommand,
  StyledLine,
  StyledHeader,
  StyledHeaderTitle,
  StyledHeaderDotList,
  StyledHeaderDotItem,
  StyledTableContainer,
  StyledTableRow,
  StyledCommandWrapper,
} from "./style";

import { CSSTransition, TransitionGroup } from "react-transition-group";

import { systemCmdList, tipCmdList } from "./commands";
import { setCaretPosition } from "./utils";

class Terminal extends PureComponent {
  static propTypes = {
    cmd: PropTypes.shape({
      dynamicList: PropTypes.object,
      staticList: PropTypes.object,
    }).isRequired,
    config: PropTypes.shape({
      initialDirectory: PropTypes.string,
      prompt: PropTypes.string,
      version: PropTypes.string,
      bootCmd: PropTypes.string,
    }),
    className: PropTypes.string,
  };

  static defaultProps = {
    className: "EasyDB",
    config: {
      initialDirectory: "src",
      prompt: "easydb> ",
      version: "1.0.0",
      // bootCmd: "intro",
      bootCmd: "welcome", // 使用新的命令
    },
  };

  historyCmdList = [];
  historyCmdIndex = 0;

  constructor(props) {
    super(props);
    this.$terminal = React.createRef();
    this.$inputWrapper = React.createRef();
    this.$inputEl = React.createRef();

    this.state = {
      cmdList: [],
      command: "",
      directory: this.props.config.initialDirectory,
      isPrinting: false,
    };

    this.historyCmdList = []; // 存储历史命令
    this.historyCmdIndex = -1; // 初始化历史索引位置

    this.supportedCmdList = [
      ...Object.keys(this.props.cmd.staticList),
      ...Object.keys(this.props.cmd.dynamicList),
    ];
    this.allCmdList = [
      ...this.supportedCmdList,
      ...Object.keys(systemCmdList)
        .map((key) => systemCmdList[key].aliasList)
        .flat(1),
    ];
  }

  componentDidMount() {
    const { bootCmd } = this.props.config;
    this.run(bootCmd).then(() => {
      const { help, clear } = systemCmdList;
      this.print([help, clear]);
      this.initializeWebSocket(); // 初始化 WebSocket 连接
      this.inputFocus();
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.command !== prevState.command) {
      this.$inputEl.current.setSelectionRange(
        this.state.cursorPosition,
        this.state.cursorPosition
      );
    }
  }

  initializeWebSocket = () => {
    this.socket = new WebSocket("ws://121.37.239.159:8081/terminal");

    this.socket.onopen = () => {
      console.log("WebSocket connection established.");
    };

    this.socket.onmessage = (message) => {
      const data = JSON.parse(message.data);
      console.log("WebSocket message received:", data);
      // 根据不同类型进行处理
      switch (data.type) {
        case "error":
          this.print({
            type: "error",
            label: "Error",
            content: data.data,
          });
          break;
        case "success":
          this.print({
            type: "success",
            label: "Success",
            content: data.data,
          });
          break;
        case "warning":
          this.print({
            type: "warning",
            label: "Warning",
            content: data.data,
          });
          break;
        default:
          this.print({
            type: "error",
            label: "Error",
            content: "Unknown message type",
          });
          break;
      }
    };

    this.socket.onclose = () => {
      console.log("WebSocket connection closed.");
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
  };

  run = (command, inputCommand = this.state.command) => {
    const { cmd } = this.props;
    this.setState({ isPrinting: true });
    return cmd.dynamicList[command]
      .run(this.print, inputCommand)
      .then(this.print)
      .catch((error) => {
        console.error(error);
        this.print({ type: "error", content: "Command execution failed" });
      })
      .finally(() => {
        this.setState({ isPrinting: false });
      });
  };

  print = (cmd) => {
    const newCmdList = Array.isArray(cmd) ? cmd : [cmd];

    this.setState((prevState) => {
      const updatedCmdList = [...prevState.cmdList];

      newCmdList.forEach((item) => {
        const content =
          typeof item === "object" && item.content ? item.content : item;

        // 判断是否为表格内容
        const isTableLine =
          typeof content === "string" &&
          content.includes("+") &&
          content.includes("|") &&
          content
            .split("\n")
            .every((line) => line.includes("+") || line.includes("|"));

        if (isTableLine) {
          const lines = content.split("\n");
          updatedCmdList.push({ content: lines, isTableLine: true });
        } else {
          updatedCmdList.push(item);
        }
      });
      return { cmdList: updatedCmdList };
    }, this.autoScroll);
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.state.command !== prevState.command) {
      this.$inputEl.current.setSelectionRange(
        this.state.cursorPosition,
        this.state.cursorPosition
      );
    }
  }

  inputFocus = () => {
    this.$inputEl.current.focus();
  };

  autoScroll = () => {
    this.$terminal.current.scrollTop = this.$inputWrapper.current.offsetTop;
  };

  handleKeyCommand = (e) => {
    const { prompt } = this.props.config;
    const { command } = this.state;
    const isCtrlCKey = e.keyCode === 67 && e.ctrlKey && !e.shiftKey;
    const isUpKey = e.keyCode === 38; // 上箭头
    const isDownKey = e.keyCode === 40; // 下箭头

    if (isCtrlCKey) {
      this.print(`${prompt}${command}`);
      this.setState({ command: "" });
      e.preventDefault();
    } else if (isUpKey) {
      // 向上遍历历史命令
      if (this.historyCmdIndex > 0) {
        this.historyCmdIndex -= 1;
        this.setState({ command: this.historyCmdList[this.historyCmdIndex] });
      }
    } else if (isDownKey) {
      // 向下遍历历史命令
      if (this.historyCmdIndex < this.historyCmdList.length - 1) {
        this.historyCmdIndex += 1;
        this.setState({ command: this.historyCmdList[this.historyCmdIndex] });
      } else {
        // 如果超出范围，清空命令行
        this.historyCmdIndex = this.historyCmdList.length;
        this.setState({ command: "" });
      }
    }

    this.autoScroll();
    this.inputFocus();
  };

  handleCommand = (e) => {
    const { prompt } = this.props.config;
    const isEnterKey = e.keyCode === 13;

    if (!isEnterKey) {
      this.handleKeyCommand(e);
      return;
    }

    const { command } = this.state;

    if (!command) {
      return;
    }

    // 1. 保存输入的命令到历史记录中
    this.historyCmdList.push(command);
    this.historyCmdIndex = this.historyCmdList.length; // 将索引重置到最新位置

    // 2. 立即将输入的命令添加到 cmdList 中
    this.setState(
      (prevState) => ({
        cmdList: [...prevState.cmdList, `${prompt}${command}`],
        command: "", // 清空输入框内容
      }),
      () => {
        // 3. 在命令行被添加到历史后，再执行命令
        this.runCommand(command);
      }
    );
  };

  runCommand = (command) => {
    // 保留原始的用户输入，以便后续发送给后端时保持大小写不变
    const originalCommand = command;

    command = command.toLowerCase();
    console.log("command:", command);
    const sanitizedCommand = command.trim().replace(/\s+/g, " ");
    const [action, commandKey] = sanitizedCommand.split(" ");

    const matchedSystemCommand = Object.values(systemCmdList).find((cmd) =>
      cmd.aliasList.includes(action)
    );

    const isStaticCommand = !!this.props.cmd.staticList[sanitizedCommand];
    const isDynamicCommand = !!this.props.cmd.dynamicList[action];

    if (matchedSystemCommand) {
      // 如果是系统命令，根据具体命令进行不同的处理
      switch (matchedSystemCommand) {
        case systemCmdList.help:
          this.handleHelp(commandKey);
          break;
        case systemCmdList.clear:
          this.handleClear();
          break;
        default:
          this.print(matchedSystemCommand);
      }
    } else if (isStaticCommand) {
      this.print(this.props.cmd.staticList[sanitizedCommand].list);
    } else if (isDynamicCommand) {
      this.run(action, commandKey);
    } else {
      // 不是系统命令，转发到后端
      this.socket.send(JSON.stringify({ command: originalCommand.trim().replace(/\s+/g, " ") }));
    }
  };

  handleHelp = (commandKey) => {
    if (commandKey) {
      const command =
        this.props.cmd.staticList[commandKey] ||
        this.props.cmd.dynamicList[commandKey];
      if (command) {
        this.print({
          type: "success",
          label: commandKey,
          content: " => " + command.description,
        });
      } else {
        this.print({
          type: "error",
          label: "Error",
          content: "Command not found.",
        });
      }
    } else {
      const staticCommands = Object.keys(this.props.cmd.staticList).map(
        (key) => {
          const command = this.props.cmd.staticList[key];
          return {
            type: "success",
            label: key,
            content: " => " + command.description,
          };
        }
      );

      const dynamicCommands = Object.keys(this.props.cmd.dynamicList).map(
        (key) => {
          const command = this.props.cmd.dynamicList[key];
          return {
            type: "success",
            label: key,
            content: " => " + command.description,
          };
        }
      );

      const allCommands = staticCommands.concat(dynamicCommands);

      this.print(allCommands);
    }
  };

  handleClear = () => {
    this.setState({ cmdList: [] });
  };

  handleInputChange = (e) => {
    const cursorPosition = e.target.selectionStart;

    // 更新命令状态和光标位置
    this.setState({
      command: e.target.value,
      cursorPosition: cursorPosition,
    });
  };

  render() {
    const {
      className,
      config: { prompt },
    } = this.props;
    const { cmdList, isPrinting, command, directory } = this.state;

    return (
      <StyledTerminalWrapper className={className}>
        <StyledHeader>
          <StyledHeaderTitle>{directory}</StyledHeaderTitle>
          <StyledHeaderDotList>
            <StyledHeaderDotItem color="red" />
            <StyledHeaderDotItem color="yellow" />
            <StyledHeaderDotItem color="green" />
          </StyledHeaderDotList>
        </StyledHeader>
        <StyledTerminal ref={this.$terminal}>
          <StyledTerminalInner onClick={this.inputFocus}>
            <TransitionGroup>
              {cmdList.map((item, index) => (
                <CSSTransition key={index} timeout={500}>
                  <StyledLine>
                    {item.isTableLine ? (
                      <StyledTableContainer>
                        {item.content.map((line, idx) => (
                          <StyledTableRow key={idx}>{line}</StyledTableRow>
                        ))}
                      </StyledTableContainer>
                    ) : typeof item === "string" ? ( // 处理简单字符串的命令行
                      <StyledCommand className="cmd">{item}</StyledCommand>
                    ) : (
                      <StyledCommandWrapper>
                        {item.time && (
                          <StyledCommand className="time">
                            {item.time}
                          </StyledCommand>
                        )}
                        {item.label && (
                          <StyledCommand className={item.type}>
                            {item.label}
                          </StyledCommand>
                        )}
                        {typeof item.content === "string" ? (
                          <StyledCommand className="cmd">
                            {item.content}
                          </StyledCommand>
                        ) : (
                          <StyledCommand className="cmd">
                            {JSON.stringify(item.content)}
                          </StyledCommand>
                        )}
                      </StyledCommandWrapper>
                    )}
                  </StyledLine>
                </CSSTransition>
              ))}
            </TransitionGroup>

            <StyledInputWrapper
              ref={this.$inputWrapper}
              onClick={this.inputFocus}
            >
              {isPrinting ? (
                <StyledLoadingCursor>.</StyledLoadingCursor>
              ) : (
                <>
                  <StyledPrompt>{prompt}</StyledPrompt>
                  <StyledCommand>{command}</StyledCommand>
                  <StyledBlinkCursor>&nbsp;</StyledBlinkCursor>
                </>
              )}
              <StyledInput
                value={command}
                onChange={this.handleInputChange}
                onKeyDown={this.handleCommand}
                autoFocus
                ref={this.$inputEl}
              />
            </StyledInputWrapper>
          </StyledTerminalInner>
        </StyledTerminal>
      </StyledTerminalWrapper>
    );
  }
}

export default Terminal;
